import { FC, useState, useCallback, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Checkbox,
  Container,
  Paper,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  Alert,
  Stack,
  Divider,
  Avatar,
  InputAdornment,
  Fade,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Badge as BadgeIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  ArrowBack as ArrowBackIcon,
  Login as LoginIcon,
  Refresh as RefreshIcon,
  HowToReg as HowToRegIcon,
  ContactMail as ContactMailIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import {
  useSearchDealerCustomersMutation,
  useSaveDealerCustomersMutation,
} from "../../../app/services/dealerCustomer";
import {
  DealerCustomer,
  DealerCustomerSearchRequest,
  DealerCustomerSaveRequest,
  DealerCustomerSaveResult,
  SignupStep,
  RegistrationResultItem,
  CustomerPortalConfig,
} from "../../../app/interfaces/dealerCustomer";

const isEmail = (value: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value);
};

const isPhoneNumber = (value: string): boolean => {
  const phoneRegex = /^[\d\s()+-]+$/;
  return phoneRegex.test(value) && value.replace(/\D/g, "").length >= 7;
};

const getCustomerDisplayName = (customer: DealerCustomer): string => {
  if (customer.isOrganization) {
    return customer.organizationName || `Organization #${customer.customerNumber}`;
  }
  const fullName = `${customer.firstName || ""} ${customer.lastName || ""}`.trim();
  return fullName || `Customer #${customer.customerNumber}`;
};

const getErrorMessage = (error: any): string => {
  if (error?.data?.message) {
    return error.data.message;
  }
  if (error?.data?.errors) {
    return Object.values(error.data.errors).flat().join(", ");
  }
  if (error?.status === 400) {
    return "Invalid request. Please check your input and try again.";
  }
  if (error?.status === 401) {
    return "Session expired. Please log in again.";
  }
  if (error?.status === 500) {
    return "Server error. Please try again later.";
  }
  if (error?.message) {
    return error.message;
  }
  return "An unexpected error occurred. Please try again.";
};

const searchValidationSchema = Yup.object({
  identifier: Yup.string()
    .required("Email or phone number is required")
    .test(
      "email-or-phone",
      "Please enter a valid email or phone number",
      (value) => {
        if (!value) return false;
        return isEmail(value) || isPhoneNumber(value);
      }
    ),
});

const CustomerCard: FC<{
  customer: DealerCustomer;
  isSelected: boolean;
  onToggle: () => void;
}> = ({ customer, isSelected, onToggle }) => {
  const displayName = customer.isOrganization
    ? customer.organizationName || `Organization #${customer.customerNumber}`
    : `${customer.firstName || ""} ${customer.lastName || ""}`.trim() ||
      `Customer #${customer.customerNumber}`;

  return (
    <Card
      variant="outlined"
      sx={{
        mb: 1.5,
        borderColor: isSelected ? "primary.main" : "divider",
        borderWidth: isSelected ? 2 : 1,
        bgcolor: isSelected ? "primary.50" : "background.paper",
        transition: "all 0.2s ease",
        "&:hover": {
          borderColor: "primary.main",
          bgcolor: isSelected ? "primary.50" : "action.hover",
        },
      }}
    >
      <CardActionArea onClick={onToggle}>
        <CardContent sx={{ py: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <Checkbox
              checked={isSelected}
              onClick={(e) => e.stopPropagation()}
              onChange={onToggle}
              sx={{ mt: -0.5 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                {customer.isOrganization ? (
                  <BusinessIcon fontSize="small" color="primary" />
                ) : (
                  <PersonIcon fontSize="small" color="secondary" />
                )}
                <Typography variant="subtitle1" fontWeight={600} noWrap>
                  {displayName}
                </Typography>
                <Chip
                  size="small"
                  label={customer.isOrganization ? "Organization" : "Individual"}
                  color={customer.isOrganization ? "primary" : "secondary"}
                  variant="outlined"
                  sx={{ ml: "auto" }}
                />
              </Box>
              <Stack spacing={0.5}>
                {customer.customerNumber && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <BadgeIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {customer.customerNumber}
                    </Typography>
                  </Box>
                )}
                {customer.email && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <EmailIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {customer.email}
                    </Typography>
                  </Box>
                )}
                {customer.phone && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PhoneIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {customer.phone}
                    </Typography>
                  </Box>
                )}
                {customer.address && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <LocationIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary" noWrap>
                      {customer.address}
                    </Typography>
                  </Box>
                )}
              </Stack>
            </Box>
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const ResultCard: FC<{ item: RegistrationResultItem }> = ({ item }) => {
  const { customer, result } = item;
  const displayName = getCustomerDisplayName(customer);

  const getStatusConfig = () => {
    if (result.success && result.isExisting) {
      return {
        color: "warning" as const,
        icon: <WarningIcon />,
        bgColor: "warning.lighter",
        borderColor: "warning.light",
      };
    }
    if (result.success) {
      return {
        color: "success" as const,
        icon: <CheckCircleIcon />,
        bgColor: "success.lighter",
        borderColor: "success.light",
      };
    }
    return {
      color: "error" as const,
      icon: <CancelIcon />,
      bgColor: "error.lighter",
      borderColor: "error.light",
    };
  };

  const config = getStatusConfig();

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 1.5,
        display: "flex",
        alignItems: "center",
        gap: 2,
        bgcolor: config.bgColor,
        borderColor: config.borderColor,
      }}
    >
      <Avatar
        sx={{
          bgcolor: `${config.color}.main`,
          width: 48,
          height: 48,
        }}
      >
        {config.icon}
      </Avatar>
      <Box sx={{ flex: 1 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          {displayName}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {result.message}
        </Typography>
      </Box>
    </Paper>
  );
};

export const DealerCustomerSignup: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [currentStep, setCurrentStep] = useState<SignupStep>(1);
  const [searchCriteria, setSearchCriteria] =
    useState<DealerCustomerSearchRequest | null>(null);
  const [customers, setCustomers] = useState<DealerCustomer[]>([]);
  const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(
    new Set()
  );
  const [registrationResults, setRegistrationResults] = useState<
    RegistrationResultItem[]
  >([]);
  const [apiError, setApiError] = useState<string | null>(null);

  const [hostOrigin, setHostOrigin] = useState<string | null>(null);
  
  const [config, setConfig] = useState<CustomerPortalConfig | null>(
    location.state?.config || null
  );

  const dealerWebsite = config?.dealerWebsite || "";
  const productId = config?.productId || "";

  // TODO: Uncomment validation before production
  useEffect(() => {
    const hostOriginParam = searchParams.get("hostOrigin");
    // if (hostOriginParam && ALLOWED_PARENT_ORIGINS.includes(hostOriginParam)) {
    //   setHostOrigin(hostOriginParam);
    // } else if (hostOriginParam) {
    //   console.error("Untrusted hostOrigin:", hostOriginParam);
    // }
    
    if (hostOriginParam) {
      setHostOrigin(hostOriginParam);
    }
  }, [searchParams]);

  useEffect(() => {
    if (config) return;

    const handleMessage = (event: MessageEvent) => {
      // TODO: Uncomment origin validation before production
      // if (hostOrigin) {
      //   const allowedOrigin = new URL(hostOrigin).origin;
      //   if (event.origin !== allowedOrigin) {
      //     console.debug("Ignored message from unexpected origin:", event.origin);
      //     return;
      //   }
      // }

      if (event.data?.type === "CUSTOMER_PORTAL_CONFIG") {
        setConfig({
          productId: event.data.productId,
          dealerWebsite: event.data.dealerWebsite,
          clientId: event.data.clientId,
          clientSecret: event.data.clientSecret,
        });
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [hostOrigin, config]);

  useEffect(() => {
    if (hostOrigin && !config) {
      window.parent.postMessage({ type: "REQUEST_CUSTOMER_PORTAL_CONFIG" }, hostOrigin);
    }
  }, [hostOrigin, config]);

  const [searchCustomers, { isLoading: isSearching }] =
    useSearchDealerCustomersMutation();
  const [saveCustomers, { isLoading: isSaving }] =
    useSaveDealerCustomersMutation();

  const formik = useFormik({
    initialValues: {
      identifier: "",
    },
    validationSchema: searchValidationSchema,
    onSubmit: async (values) => {
      setApiError(null);

      const identifierIsEmail = isEmail(values.identifier);
      
      const request: DealerCustomerSearchRequest = {
        dealerWebsite: dealerWebsite,
        ...(identifierIsEmail
          ? { email: values.identifier }
          : { phoneNumber: values.identifier }),
      };

      try {
        const response = await searchCustomers(request).unwrap();

        if (response.success) {
          if (response.customers?.length === 0) {
            toast.info(response.message || "No customers found matching your criteria");
          } else {
            setSearchCriteria(request);
            setCustomers(response.customers || []);
            setSelectedIndexes(new Set());
            setCurrentStep(2);
          }
        } else {
          setApiError(response.message || "Search failed. Please try again.");
        }
      } catch (error: any) {
        console.error("Search error:", error);
        const errorMessage = getErrorMessage(error);
        setApiError(errorMessage);
        toast.error(errorMessage);
      }
    },
  });

  const toggleCustomerSelection = useCallback((index: number) => {
    setSelectedIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  }, []);

  const toggleSelectAll = useCallback(() => {
    if (selectedIndexes.size === customers.length) {
      setSelectedIndexes(new Set());
    } else {
      setSelectedIndexes(new Set(customers.map((_, i) => i)));
    }
  }, [customers, selectedIndexes.size]);

  const handleRegister = async () => {
    if (!searchCriteria || selectedIndexes.size === 0) return;

    setApiError(null);
    const selectedCustomers = customers.filter((_, i) =>
      selectedIndexes.has(i)
    );

    const requests: DealerCustomerSaveRequest[] = selectedCustomers.map(
      (customer) => ({
        dealerWebsite: searchCriteria.dealerWebsite,
        customerId: customer.customerId,
        email: customer.email || searchCriteria.email,
        phoneNumber: customer.phone || searchCriteria.phoneNumber,
        firstName: customer.firstName,
        lastName: customer.lastName,
        productId: productId,
      })
    );

    try {
      const results = await saveCustomers(requests).unwrap();

      const resultItems: RegistrationResultItem[] = selectedCustomers.map(
        (customer, index) => ({
          customer,
          result: results[index] || {
            success: false,
            message: "No response received",
            isExisting: false,
          },
        })
      );

      setRegistrationResults(resultItems);
      setCurrentStep(3);

      const successCount = results.filter(
        (r: DealerCustomerSaveResult) => r.success && !r.isExisting
      ).length;
      const existingCount = results.filter(
        (r: DealerCustomerSaveResult) => r.success && r.isExisting
      ).length;
      const failedCount = results.filter((r: DealerCustomerSaveResult) => !r.success).length;

      if (existingCount > 0 && successCount === 0 && failedCount === 0) {
        toast.info("User already exists. Please sign in to continue.");
      } else if (successCount > 0 && existingCount === 0 && failedCount === 0) {
        toast.success("Registration successful! You can now sign in.");
      } else if (failedCount > 0 && successCount === 0 && existingCount === 0) {
        toast.error("Registration failed. Please try again.");
      } else if (failedCount === 0) {
        toast.success("Registration completed successfully!");
      } else {
        toast.warning("Registration completed with some issues.");
      }
    } catch (error: any) {
      console.error("Registration error:", error);
      const errorMessage = getErrorMessage(error);
      setApiError(errorMessage);
      toast.error(errorMessage);
    }
  };

  const handleBackToSearch = () => {
    setCurrentStep(1);
    setApiError(null);
  };

  const handleStartOver = () => {
    setCurrentStep(1);
    setSearchCriteria(null);
    setCustomers([]);
    setSelectedIndexes(new Set());
    setRegistrationResults([]);
    setApiError(null);
    formik.resetForm();
  };

  const handleGoToLogin = () => {
    navigate("/otp-request" + (hostOrigin ? `?hostOrigin=${encodeURIComponent(hostOrigin)}` : ""), { state: { config } });
  };

  const getResultSummary = () => {
    const success = registrationResults.filter(
      (r) => r.result.success && !r.result.isExisting
    ).length;
    const existing = registrationResults.filter(
      (r) => r.result.success && r.result.isExisting
    ).length;
    const failed = registrationResults.filter((r) => !r.result.success).length;
    return { success, existing, failed };
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: "grey.100",
        py: 2,
        boxSizing: "border-box",
      }}
    >
      <Container maxWidth="sm">
        <Paper elevation={3} sx={{ borderRadius: 3, overflow: "hidden" }}>
          {/* Header */}
          <Box
            sx={{
              bgcolor: "primary.main",
              color: "primary.contrastText",
              p: 3,
              textAlign: "center",
            }}
          >
            <Avatar
              sx={{
                bgcolor: "primary.light",
                width: 56,
                height: 56,
                mx: "auto",
                mb: 2,
              }}
            >
              <HowToRegIcon fontSize="large" />
            </Avatar>
            <Typography variant="h5" fontWeight={600}>
              Welcome back
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 1 }}>
              Sign in to access your account
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ p: 3 }}>
            {/* Error Alert */}
            {apiError && (
              <Alert
                severity="error"
                onClose={() => setApiError(null)}
                sx={{ mb: 3 }}
              >
                {apiError}
              </Alert>
            )}

            {currentStep === 1 && (
              <Fade in={currentStep === 1}>
                <Box component="form" onSubmit={formik.handleSubmit}>
                  <TextField
                    fullWidth
                    size="medium"
                    label="Email or Phone Number"
                    name="identifier"
                    placeholder="Enter your Email or Phone Number"
                    value={formik.values.identifier}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.identifier &&
                      Boolean(formik.errors.identifier)
                    }
                    helperText={
                      formik.touched.identifier && formik.errors.identifier
                    }
                    sx={{ mb: 2 }}
                    autoFocus
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ContactMailIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />

                <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={isSearching || !formik.values.identifier.trim()}
                    startIcon={
                      isSearching ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <SearchIcon />
                      )
                    }
                    sx={{ mt: 1, py: 1.5 }}
                  >
                    {isSearching ? "Searching..." : "Find My Account"}
                  </Button>

                  <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 3 }}>
                    Already registered?{" "}
                    <Typography
                      component="span"
                      variant="body2"
                      color="primary"
                      sx={{ cursor: "pointer", textDecoration: "underline" }}
                      onClick={handleGoToLogin}
                    >
                      Sign In
                    </Typography>
                  </Typography>
                </Box>
              </Fade>
            )}

            {currentStep === 2 && (
              <Fade in={currentStep === 2}>
                <Box>
                  {isSearching ? (
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        py: 6,
                      }}
                    >
                      <CircularProgress size={48} />
                      <Typography color="text.secondary" sx={{ mt: 2 }}>
                        Searching for customers...
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        We found {customers.length} account{customers.length > 1 ? "s" : ""} matching your information.
                        Select the one{customers.length > 1 ? "(s)" : ""} you want to register.
                      </Alert>

                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          mb: 2,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          bgcolor: "grey.50",
                        }}
                      >
                        <Typography variant="body2" fontWeight={500}>
                          {selectedIndexes.size} of {customers.length} selected
                        </Typography>
                        {customers.length > 1 && (
                          <Button size="small" onClick={toggleSelectAll}>
                            {selectedIndexes.size === customers.length
                              ? "Deselect All"
                              : "Select All"}
                          </Button>
                        )}
                      </Paper>

                    <Box
                      sx={{
                        maxHeight: 350,
                        overflowY: "auto",
                        pr: 1,
                        mr: -1,
                      }}
                    >
                      {customers.map((customer, index) => (
                        <CustomerCard
                          key={`${customer.customerId}-${index}`}
                          customer={customer}
                          isSelected={selectedIndexes.has(index)}
                          onToggle={() => toggleCustomerSelection(index)}
                        />
                      ))}
                    </Box>

                      <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                        <Button
                          variant="outlined"
                          startIcon={<ArrowBackIcon />}
                          onClick={handleBackToSearch}
                          disabled={isSaving}
                          fullWidth
                        >
                          Back
                        </Button>
                        <Tooltip
                          title={selectedIndexes.size === 0 ? "Select at least one account" : ""}
                        >
                          <span style={{ width: "100%" }}>
                            <Button
                              variant="contained"
                              startIcon={
                                isSaving ? (
                                  <CircularProgress size={20} color="inherit" />
                                ) : (
                                  <HowToRegIcon />
                                )
                              }
                              onClick={handleRegister}
                              disabled={selectedIndexes.size === 0 || isSaving}
                              fullWidth
                            >
                              {isSaving
                                ? "Registering..."
                                : `Register${selectedIndexes.size > 0 ? ` (${selectedIndexes.size})` : ""}`}
                            </Button>
                          </span>
                        </Tooltip>
                      </Stack>
                    </>
                  )}
                </Box>
              </Fade>
            )}

            {currentStep === 3 && (
              <Fade in={currentStep === 3}>
                <Box>
                  {(() => {
                    const summary = getResultSummary();
                    const isAllExisting = summary.existing > 0 && summary.success === 0 && summary.failed === 0;
                    
                    return (
                      <>
                        <Alert
                          severity={
                            isAllExisting
                              ? "info"
                              : summary.failed === 0
                              ? "success"
                              : summary.success > 0
                              ? "warning"
                              : "error"
                          }
                          sx={{ mb: 3 }}
                        >
                          {isAllExisting
                            ? "User already exists. Please sign in to continue."
                            : summary.failed === 0
                            ? "Registration completed successfully!"
                            : summary.success > 0
                            ? "Registration completed with some issues."
                            : "Registration failed. Please try again."}
                        </Alert>

                        {/* Only show detailed summary if not all existing */}
                        {!isAllExisting && (
                          <Paper
                            variant="outlined"
                            sx={{
                              p: 2,
                              mb: 3,
                              bgcolor: "grey.50",
                            }}
                          >
                            <Stack
                              direction="row"
                              divider={<Divider orientation="vertical" flexItem />}
                              spacing={2}
                              justifyContent="space-around"
                            >
                              <Box sx={{ textAlign: "center" }}>
                                <Typography
                                  variant="h5"
                                  fontWeight={700}
                                  color="success.main"
                                >
                                  {summary.success}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Registered
                                </Typography>
                              </Box>
                              {summary.existing > 0 && (
                                <Box sx={{ textAlign: "center" }}>
                                  <Typography
                                    variant="h5"
                                    fontWeight={700}
                                    color="info.main"
                                  >
                                    {summary.existing}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Already Existed
                                  </Typography>
                                </Box>
                              )}
                              {summary.failed > 0 && (
                                <Box sx={{ textAlign: "center" }}>
                                  <Typography
                                    variant="h5"
                                    fontWeight={700}
                                    color="error.main"
                                  >
                                    {summary.failed}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    Failed
                                  </Typography>
                                </Box>
                              )}
                            </Stack>
                          </Paper>
                        )}

                        {!isAllExisting && (
                          <>
                            <Typography
                              variant="subtitle2"
                              color="text.secondary"
                              sx={{ mb: 2 }}
                            >
                              Registration Details
                            </Typography>

                            <Box
                              sx={{
                                maxHeight: 300,
                                overflowY: "auto",
                                pr: 1,
                                mr: -1,
                              }}
                            >
                              {registrationResults.map((item, index) => (
                                <ResultCard key={index} item={item} />
                              ))}
                            </Box>
                          </>
                        )}

                        <Stack direction="row" spacing={2} sx={{ mt: 3 }}>
                          <Button
                            variant="outlined"
                            startIcon={<RefreshIcon />}
                            onClick={handleStartOver}
                            fullWidth
                          >
                            Start Over
                          </Button>
                          <Button
                            variant="contained"
                            startIcon={<LoginIcon />}
                            onClick={handleGoToLogin}
                            fullWidth
                          >
                            Continue to Sign In
                          </Button>
                        </Stack>
                      </>
                    );
                  })()}
                </Box>
              </Fade>
            )}
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default DealerCustomerSignup;